import React, { useEffect } from 'react';
import { Switch, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useLazyQuery } from '@apollo/client';

import Queries from 'components/Common/Queries';
import { UserRoutes } from './UserRoutes';
import { useProfile } from 'hooks/useProfile';
import { adduser } from 'Redux/AuthSlice/AuthSlice';
import FullPageLoader from 'sharedComponents/FullPageLoader/FullPageLoader';
import NotificationHandler from './UserRoutes/NotificationHandler';
import { rememberPostLoginRedirect } from 'utils/url';

const Routes = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch()

  const [getIsUserProfileComplete, { loading, data }] = useLazyQuery(Queries.GET_USER_IS_PROFILE_COMPLETE, {
    fetchPolicy: "no-cache"
  });

  const profile = useProfile();
  const { login, oidcUser, isLoading } = profile;

  // redirect to the login and than home if the user is already logged in
  const loggedInLocal = localStorage.getItem("authPersist")
  const isLoggingInForPayment = localStorage.getItem("userLoggingInForPayment") === "true"; // payment: fetching email via login

  // get profile completed data only the first time
  useEffect(() => {
    const fetchData = async () => {
      await getIsUserProfileComplete();
    };
    fetchData();
  }, []);

  useEffect(() => {

    // Wait until the stored user has loaded, else we may send a logged-in user to login.
    if (isLoading) return;

    // Only auto-login when we are not already authenticated. `signinRedirect()`
    // (react-oidc-context) has no "already signed in" guard, so calling it while a
    // valid user exists would bounce through the IdP on every navigation -> loop.
    if (loggedInLocal === "true" && !oidcUser) {

      // Remember the current page so we return here after login, not to home.
      rememberPostLoginRedirect(pathname);

      // do not allow auto login unless user clicks on the login on payment screen
      if (pathname === "/payment") {
        isLoggingInForPayment && login(); // user has clicked on the login
      }
      // call login if the user is already logged in and the user is not on the payment page (opened the route in new tab)
      else {
        // reset authpersist on every login. If login successfull then we reset it below, if login fails, we dont stuck in loop
        localStorage.removeItem("authPersist");
        login();
      }

    }
  }, [isLoading, oidcUser])

  // update persist true
  useEffect(() => {
    if (oidcUser?.access_token) {
      localStorage.setItem("authPersist", "true")
    }
  }, [oidcUser?.access_token])

  useEffect(() => {
    if (data) {

      const userdata = {
        id: data.getUser.user?.id,
        email: data.getUser.user?.email,
        firstName: data.getUser.user?.firstName,
        lastName: data.getUser.user?.lastName,
        name: data.getUser.user?.name,
        isSuperAdmin: data.getUser.user?.isSuperAdmin,
        unreadCount: data?.isProfileComplete?.unreadMessageCount,
        isAuthenticated: data.isProfileComplete.isAuthenticated,
        isCompleted: data.isProfileComplete.isCompleted,
        isPhoneNumberConfirmed: data.isProfileComplete.isPhoneNumberConfirmed,
        totalNotifications: data.isProfileComplete.unviewedCount,
        totalAppointments: data.isProfileComplete.upComingAppointmentsCount,
      }

      const obj = {
        authinfo: profile.oidcUser,
        userinfo: {
          ...userdata
        }
      }
      dispatch(adduser(obj));
    }
  }, [data])

  return (
    <>
      <NotificationHandler profile={profile} />
      {loading && <FullPageLoader />}
      <Switch>
        <UserRoutes profile={profile} />
      </Switch>
    </>
  );
};

export default Routes;
